import { TagsStructure } from '@/hooks/api/use-table-details-query'
import React from 'react'
import {
  ColumnEditData,
  EditableFieldName,
  EditingHandlers,
  EditingRefs,
  EditingState,
  TableEditData,
} from '../types'
import { createTextareaKey, flattenTags } from '../utils'

export const useEditing = (
  memoizedColumns: any[],
  memoizedTables?: any[],
  isTableVersionChanged?: boolean,
) => {
  // State
  const [editingCell, setEditingCell] = React.useState<string | null>(null)
  const [editingField, setEditingField] = React.useState<EditableFieldName | null>(null)
  const [editMode, setEditMode] = React.useState(false)
  const [cursorPosition, setCursorPosition] = React.useState<number>(0)
  const [tables, setTables] = React.useState<Record<string, TableEditData>>({})
  const [columns, setColumns] = React.useState<Record<string, ColumnEditData>>({})

  // Refs
  const textareaRefs = React.useRef<Record<string, HTMLTextAreaElement>>({})

  // Handle field editing for any editable field
  const handleFieldEdit = React.useCallback(
    (columnName: string, fieldName: EditableFieldName, value: string) => {
      const textareaKey = createTextareaKey(columnName, fieldName)
      const textarea = textareaRefs.current[textareaKey]

      if (textarea) {
        const cursorPosition = textarea.selectionEnd

        // Handle table level editing
        if (fieldName === 'table_description') {
          setTables((prev) => ({
            ...prev,
            [columnName]: {
              ...prev[columnName],
              table_description: value,
            },
          }))
        } else {
          // Handle column-level editing
          const originalColumn = memoizedColumns.find((col) => col.column_name === columnName)
          if (!originalColumn) return

          // Update the edited columns with the new value for the specific field
          setColumns((prev) => {
            const existingColumnData = prev[columnName] || {}

            // Only update if value actually changed
            if (existingColumnData[fieldName] === value) {
              return prev // No changes needed
            }

            return {
              ...prev,
              [columnName]: {
                ...existingColumnData,
                [fieldName]: value,
              },
            }
          })
        }

        // Store cursor position for restoration after state update
        setCursorPosition(cursorPosition)
      }
    },
    [memoizedColumns],
  )

  // Handle cell click to start editing
  const handleCellClick = React.useCallback(
    (name: string, fieldName?: EditableFieldName) => {
      // If no fieldName is provided, default to column_description for backward compatibility
      const targetField = fieldName || 'column_description'

      // Initialize the edited data if not already set
      if (targetField === 'table_description') {
        // Handle table description editing
        if (!tables[name]) {
          const currentValue = (() => {
            // For table descriptions, get the value from the table data
            if (memoizedTables) {
              const table = memoizedTables.find((table) => table.table_name === name)
              return table?.table_description || ''
            }
            return ''
          })()
          setTables((prev) => ({
            ...prev,
            [name]: {
              table_description: currentValue,
            },
          }))
        }
      } else if (!columns[name]) {
        // Handle column editing - initialize empty object for this column
        setColumns((prev) => ({
          ...prev,
          [name]: {},
        }))
      }

      setEditingCell(name)
      setEditingField(targetField) // Set which field is being edited
      setEditMode(true) // Enter edit mode

      // Set cursor position to end of text for better UX
      const currentValue = (() => {
        if (targetField === 'table_description') {
          // For table descriptions, check tables state first
          return tables[name]?.table_description || ''
        } else {
          // For column fields, check columns state first
          if (columns[name]) {
            return columns[name][targetField] || ''
          }

          // Fallback to original column data
          const column = memoizedColumns.find((col) => col.column_name === name)
          if (!column) return ''

          switch (targetField) {
            case 'column_description':
              return column.column_description || ''
            case 'business_definition':
              return column.business_definition || ''
            case 'business_rules':
              return column.business_rules || ''
            default:
              return ''
          }
        }
      })()

      setCursorPosition(currentValue.length)
    },
    [memoizedColumns, columns, tables],
  )

  // Helper function to check if column data has changes
  const hasColumnChanges = React.useCallback((editedData: ColumnEditData, originalColumn: any) => {
    // Compare tags by flattening them to arrays for comparison
    const editedTags = editedData.tags ? flattenTags(editedData.tags) : []
    const originalTags = originalColumn.tags ? flattenTags(originalColumn.tags) : []
    const tagsChanged = JSON.stringify(editedTags) !== JSON.stringify(originalTags)

    return (
      editedData.column_description !== (originalColumn.column_description || '') ||
      editedData.business_definition !== (originalColumn.business_definition || '') ||
      editedData.business_rules !== (originalColumn.business_rules || '') ||
      tagsChanged
    )
  }, [])

  // Helper function to check if table data has changes
  const hasTableChanges = React.useCallback((editedData: TableEditData, originalTable: any) => {
    return editedData.table_description !== (originalTable.table_description || '')
  }, [])

  // Helper function to clean up column edits
  const cleanupColumnEdits = React.useCallback(
    (prev: Record<string, ColumnEditData>) => {
      const cleaned: Record<string, ColumnEditData> = {}

      Object.entries(prev).forEach(([columnName, editedData]) => {
        const originalColumn = memoizedColumns.find((col) => col.column_name === columnName)
        if (!originalColumn) return

        if (hasColumnChanges(editedData, originalColumn)) {
          cleaned[columnName] = editedData
        }
      })

      return cleaned
    },
    [memoizedColumns, hasColumnChanges],
  )

  // Helper function to clean up table edits
  const cleanupTableEdits = React.useCallback(
    (prev: Record<string, TableEditData>) => {
      const cleaned: Record<string, TableEditData> = {}

      Object.entries(prev).forEach(([tableName, editedData]) => {
        if (memoizedTables) {
          const originalTable = memoizedTables.find((table) => table.table_name === tableName)
          if (!originalTable) return

          if (hasTableChanges(editedData, originalTable)) {
            cleaned[tableName] = editedData
          }
        }
      })

      return cleaned
    },
    [memoizedTables, hasTableChanges],
  )

  // Helper function to clean up textarea refs
  const cleanupTextareaRefs = React.useCallback(() => {
    setTimeout(() => {
      Object.keys(textareaRefs.current).forEach((key) => {
        if (key !== editingCell) {
          delete textareaRefs.current[key]
        }
      })
    }, 100)
  }, [editingCell])

  // Handle textarea blur to stop editing
  const handleTextareaBlur = React.useCallback(() => {
    setEditingCell(null)
    setEditingField(null) // Clear editing field
    setCursorPosition(0) // Reset cursor position

    // Clean up any edited columns that are the same as original
    if (!isTableVersionChanged) {
      setColumns(cleanupColumnEdits)
    }

    // Clean up any edited table descriptions that are the same as original
    setTables(cleanupTableEdits)

    // Clean up the ref when editing stops
    cleanupTextareaRefs()
  }, [cleanupColumnEdits, cleanupTableEdits, cleanupTextareaRefs])

  // Handle tag deletion
  const handleTagDelete = React.useCallback(
    (columnName: string, tagToDelete: string) => {
      setEditMode(true) // Enable edit mode when tags are modified
      setColumns((prev) => {
        const originalColumn = memoizedColumns.find((col) => col.column_name === columnName)
        const existingColumnData = prev[columnName] || {}

        // Get current tags from edited state or fallback to original
        const currentTags = existingColumnData.tags || {
          PII_DG: originalColumn?.tags?.PII_DG || [],
          DATA_QUALITY_TAGS: originalColumn?.tags?.DATA_QUALITY_TAGS || [],
        }

        const piiTags = currentTags.PII_DG || []
        const dqTags = currentTags.DATA_QUALITY_TAGS || []

        const isPiiTag = piiTags.some((item: any) => item.tag === tagToDelete)
        const isDqTag = dqTags.some((item: any) => item.tag === tagToDelete)

        // Only create new object if we found the tag to delete
        if (!isPiiTag && !isDqTag) {
          return prev // No changes needed
        }

        // Preserve all existing tag categories and only modify the specific category
        const updatedTags: TagsStructure = { ...currentTags }

        if (isPiiTag) {
          updatedTags.PII_DG = piiTags.filter((item: any) => item.tag !== tagToDelete)
        } else if (isDqTag) {
          updatedTags.DATA_QUALITY_TAGS = dqTags.filter((item: any) => item.tag !== tagToDelete)
        }

        return {
          ...prev,
          [columnName]: {
            ...existingColumnData,
            tags: updatedTags,
          },
        }
      })
    },
    [memoizedColumns],
  )

  const handleTableVersionChange = React.useCallback(() => {
    const cols: Record<string, any> = {}
    memoizedColumns.forEach((column) => {
      const { column_name, ...columnData } = column || {}
      cols[column_name] = { ...columnData }
    })
    setColumns(cols)
    setEditMode(true)
  }, [memoizedColumns])

  // Clear all local edits
  const clearLocalEdits = React.useCallback(() => {
    setColumns({})
    setTables({})
    setEditMode(false) // Exit edit mode
    setEditingCell(null) // Clear editing cell
    setEditingField(null) // Clear editing field
    setCursorPosition(0) // Reset cursor position
  }, [])

  // Restore cursor position after state updates using useLayoutEffect
  React.useLayoutEffect(() => {
    if (editingCell && editingField && cursorPosition > 0) {
      // Find the active textarea for the specific field being edited
      const textareaKey = createTextareaKey(editingCell, editingField)
      const activeTextarea = textareaRefs.current[textareaKey]

      if (activeTextarea && document.contains(activeTextarea)) {
        // Position cursor at the calculated position or end of text
        const position = cursorPosition > 0 ? cursorPosition : activeTextarea.value.length
        activeTextarea.setSelectionRange(position, position)
        activeTextarea.focus()
      }
    }
  }, [editingCell, editingField, cursorPosition])

  // Return state and handlers
  const editingState: EditingState = {
    editingCell,
    editingField,
    editMode,
    cursorPosition,
    tables,
    columns,
  }

  const editingHandlers: EditingHandlers = {
    handleFieldEdit,
    handleCellClick,
    handleTextareaBlur,
    clearLocalEdits,
    handleTagDelete,
    handleTableVersionChange,
  }

  const editingRefs: EditingRefs = {
    textareaRefs,
  }

  return {
    editingState,
    editingHandlers,
    editingRefs,
  }
}
